import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { MultiSelect } from '@/components/ui/multi-select';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { 
  X, 
  Settings, 
  Building2, 
  FileText,
  CheckSquare,
  Edit3,
  Save,
  Upload,
  Download,
  Plus,
  Trash2,
  Printer,
  Clock,
  Calendar,
  Repeat,
  User
} from 'lucide-react';

function MaintenanceTaskDetail({ task, onClose, onUpdate, user, readOnly = false }) {
  const { t } = useLanguage();
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Debug: Log the task object to see what data we have
  console.log('MaintenanceTaskDetail received task:', task);
  console.log('Task assignees:', task.assignees);
  console.log('Task assignee_names:', task.assignee_names);
  
  const [taskData, setTaskData] = useState({
    title: task.title,
    department_id: task.department_id || task.department || '',
    department_name: task.departmentName || task.department_name || task.department || 'N/A',
    machine_id: task.machine_id || task.machine || '',
    machine_name: task.machineName || task.machine_name || task.machine || 'N/A',
    assignees: task.assignees || [],  // Multiple assignees array
    assignee_names: task.assignee_names || [],  // Multiple assignee names
    frequency: task.frequency || 'Daily',
    dayOfWeek: task.dayOfWeek || task.day_of_week || 'Monday', // For weekly tasks
    monthlyPeriod: task.monthlyPeriod || task.monthly_period || 'start', // For monthly tasks
    yearlyMonth: task.yearlyMonth || task.yearly_month || 'January', // For yearly tasks
    time: task.time || '08:00',
    priority: task.priority || 'Medium',
    notes: task.notes || '',
    safety_notes: task.safety_notes || ''
  });
  const [checklist, setChecklist] = useState(task.checklist || []);
  const [files, setFiles] = useState(task.files || []);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [machines, setMachines] = useState([]);
  const [isChecklistSaving, setIsChecklistSaving] = useState(false);
  const abortRef = useRef(null);
  
  // Generate unique cache key for this maintenance task
  const cacheKey = `equiptrack:checklist:maintenance:${task.id || 'sample'}`;
  
  // Load checklist from localStorage on mount (check for unsaved changes)
  useEffect(() => {
    const savedDraft = localStorage.getItem(cacheKey);
    if (savedDraft) {
      try {
        const draftChecklist = JSON.parse(savedDraft);
        // Only use draft if it's newer than current checklist
        if (draftChecklist.length !== checklist.length || 
            JSON.stringify(draftChecklist) !== JSON.stringify(checklist)) {
          setChecklist(draftChecklist);
        }
      } catch (error) {
        console.error('Failed to parse checklist draft:', error);
      }
    }
  }, []);
  
  useEffect(() => {
    return () => {
      // Cancel any in-flight save on unmount
      abortRef.current?.abort();
    };
  }, []);
  
  // Sync files state when task prop changes
  useEffect(() => {
    console.log('Task prop changed, files:', task.files);
    if (task.files && Array.isArray(task.files)) {
      console.log('Setting files from task:', task.files);
      setFiles(task.files);
    }
  }, [task.files]);

  // Fetch users, departments for dropdowns
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/assignees`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        setUsers(response.data);
      } catch (error) {
        console.error('Error fetching assignees:', error);
      }
    };
    
    const fetchDepartments = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/departments`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        setDepartments(response.data);
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    };
    
    fetchUsers();
    fetchDepartments();
  }, []);
  
  // Fetch machines when department changes in edit mode
  useEffect(() => {
    if (editMode && taskData.department_id) {
      const fetchMachines = async () => {
        try {
          const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/machines?department_id=${taskData.department_id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          setMachines(response.data);
        } catch (error) {
          console.error('Error fetching machines:', error);
        }
      };
      fetchMachines();
    }
  }, [editMode, taskData.department_id]);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this maintenance task? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      
      // Delete from backend API
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/work-orders/${task.id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      toast.success('Maintenance task deleted successfully');
      
      if (onUpdate) {
        onUpdate(null); // Signal that the task was deleted
      }
      onClose();
    } catch (error) {
      console.error('Failed to delete maintenance task:', error);
      toast.error('Failed to delete maintenance task');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    
    try {
      console.log('MaintenanceTaskDetail handleSave - Current state:', {
        taskId: task.id,
        department_id: taskData.department_id,
        department_name: taskData.department_name,
        machine_id: taskData.machine_id,
        machine_name: taskData.machine_name,
        assignees: taskData.assignees
      });
      
      // Prepare update data
      const updateData = {
        title: taskData.title,
        priority: taskData.priority,
        assignees: taskData.assignees.filter(id => id !== 'none'),  // Multiple assignees
        department_id: taskData.department_id || null,
        machine_id: taskData.machine_id || null,
        frequency: taskData.frequency,
        day_of_week: taskData.dayOfWeek,
        monthly_period: taskData.monthlyPeriod,
        yearly_month: taskData.yearlyMonth,
        time: taskData.time,
        notes: taskData.notes,
        safety_notes: taskData.safety_notes,
        checklist: checklist,
        files: files  // Include files in the update
      };
      
      console.log('Sending update to API:', updateData);
      
      // Determine if this is a task instance or work order based on the presence of work_order_id
      const isTaskInstance = !!task.work_order_id;
      const endpoint = isTaskInstance 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/task-instances/${task.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/work-orders/${task.id}`;
      
      console.log('Saving to endpoint:', endpoint, 'isTaskInstance:', isTaskInstance);
      
      // Save to backend API
      const response = await axios.put(
        endpoint,
        updateData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      console.log('Backend response:', response.data);
      
      // Clear localStorage draft after successful save
      localStorage.removeItem(cacheKey);
      
      setEditMode(false);
      
      // Update local state with ALL response data
      setTaskData({
        title: response.data.title,
        department_id: response.data.department_id || '',
        department_name: response.data.department_name || 'N/A',
        machine_id: response.data.machine_id || '',
        machine_name: response.data.machine_name || 'N/A',
        assignees: response.data.assignees || [],
        assignee_names: response.data.assignee_names || [],
        frequency: response.data.frequency || 'Daily',
        dayOfWeek: response.data.day_of_week || 'Monday',
        monthlyPeriod: response.data.monthly_period || 'start',
        time: response.data.time || '08:00',
        priority: response.data.priority || 'Medium',
        notes: response.data.notes || '',
        safety_notes: response.data.safety_notes || ''
      });
      
      // Update files if present in response
      if (response.data.files) {
        setFiles(response.data.files);
      }
      
      // Update checklist if present in response
      if (response.data.checklist) {
        setChecklist(response.data.checklist);
      }
      
      // Dispatch event to notify Calendar and DailyTasks
      const updateEvent = new CustomEvent('workOrderUpdated', {
        detail: response.data  // Send the complete updated task data
      });
      window.dispatchEvent(updateEvent);
      
      if (onUpdate) {
        console.log('Calling onUpdate with updated task');
        onUpdate(response.data);
      }
      
      toast.success('Maintenance task updated successfully!');
    } catch (error) {
      console.error('Save error:', error);
      console.error('Error response:', error.response?.data);
      toast.error('Failed to update maintenance task');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const printContent = generatePrintContent();
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const generatePrintContent = () => {
    const completedItems = checklist.filter(item => item.completed).length;
    const totalItems = checklist.length;
    
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              line-height: 1.4; 
            }
            .header { 
              border-bottom: 2px solid #333; 
              padding-bottom: 10px; 
              margin-bottom: 20px; 
            }
            .title { 
              font-size: 24px; 
              font-weight: bold; 
              margin-bottom: 5px; 
            }
            .subtitle { 
              color: #666; 
              font-size: 14px; 
            }
            .section { 
              margin-bottom: 20px; 
            }
            .section-title { 
              font-size: 16px; 
              font-weight: bold; 
              margin-bottom: 10px; 
              border-bottom: 1px solid #ddd; 
              padding-bottom: 5px; 
            }
            .info-grid { 
              display: grid; 
              grid-template-columns: 1fr 1fr; 
              gap: 10px; 
              margin-bottom: 15px; 
            }
            .info-item { 
              display: flex; 
            }
            .info-label { 
              font-weight: bold; 
              min-width: 100px; 
            }
            .checklist-item { 
              margin: 8px 0; 
              display: flex; 
              align-items: center; 
            }
            .checkbox { 
              width: 16px; 
              height: 16px; 
              border: 1px solid #333; 
              margin-right: 10px; 
              display: inline-block; 
            }
            .checkbox.checked::after { 
              content: '✓'; 
              font-weight: bold; 
              display: block; 
              text-align: center; 
              line-height: 14px; 
            }
            .signature-section { 
              margin-top: 40px; 
              display: grid; 
              grid-template-columns: 1fr 1fr; 
              gap: 40px; 
            }
            .signature-box { 
              border-top: 1px solid #333; 
              padding-top: 5px; 
              text-align: center; 
            }
            @media print { 
              body { margin: 0; } 
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title">Maintenance Work Order</div>
          </div>

          <div class="section">
            <div class="section-title">Task Information</div>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">Task:</span>
                <span>${taskData.title}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Department:</span>
                <span>${taskData.department_name}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Date:</span>
                <span>${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Machine:</span>
                <span>${taskData.machine_name}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Assignees:</span>
                <span>${taskData.assignee_names && taskData.assignee_names.length > 0 ? taskData.assignee_names.join(', ') : 'Not assigned'}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Frequency:</span>
                <span>${taskData.frequency}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Priority:</span>
                <span>${taskData.priority}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Scheduled Time:</span>
                <span>${taskData.time}</span>
              </div>
            </div>
          </div>

          ${taskData.notes ? `
            <div class="section">
              <div class="section-title">Instructions</div>
              <p>${taskData.notes}</p>
            </div>
          ` : ''}

          ${taskData.safety_notes ? `
            <div class="section">
              <div class="section-title">Safety Notes</div>
              <p style="background: #fff3cd; padding: 10px; border: 1px solid #ffeaa7; border-radius: 4px;">
                ${taskData.safety_notes}
              </p>
            </div>
          ` : ''}

          <div class="section">
            <div class="section-title" style="color: #dc3545;">Safety Reminder</div>
            <p style="background: #f8d7da; padding: 10px; border: 1px solid #f5c6cb; border-radius: 4px; font-weight: bold; color: #721c24;">
              Lock Down Power Supply and Use Safety Gear
            </p>
          </div>

          <div class="section">
            <div class="section-title">Checklist (${completedItems}/${totalItems} completed)</div>
            ${checklist.map(item => `
              <div class="checklist-item">
                <span class="checkbox ${item.completed ? 'checked' : ''}"></span>
                <span>${item.text}</span>
              </div>
            `).join('')}
            ${checklist.length === 0 ? '<p>No checklist items defined.</p>' : ''}
          </div>

          <div class="section">
            <div class="section-title">Notes/Comments</div>
            <div style="border: 1px solid #ddd; min-height: 80px; padding: 10px;">
              
            </div>
          </div>

          <div class="signature-section">
            <div class="signature-box">
              <div>Technician Signature</div>
            </div>
            <div class="signature-box">
              <div>Supervisor Signature</div>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  // Removed old functions - using async versions below

  const handleChecklistToggle = (itemId, completed) => {
    // Update local state only (no auto-save)
    const updatedChecklist = checklist.map(item => 
      item.id === itemId 
        ? { 
            ...item, 
            completed, 
            completed_by: completed ? user.username : null,
            completed_at: completed ? new Date().toISOString() : null
          } 
        : item
    );
    
    setChecklist(updatedChecklist);
    // Save draft to localStorage for recovery
    localStorage.setItem(cacheKey, JSON.stringify(updatedChecklist));
  };

  const addChecklistItem = () => {
    if (!newChecklistItem.trim()) return;
    
    const newItem = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // More unique ID
      text: newChecklistItem.trim(),
      completed: false,
      created_at: new Date().toISOString(),
      created_by: user.username
    };
    
    const updatedChecklist = [...checklist, newItem];
    setChecklist(updatedChecklist);
    setNewChecklistItem('');
    // Save draft to localStorage
    localStorage.setItem(cacheKey, JSON.stringify(updatedChecklist));
    toast.success('Checklist item added');
  };

  const removeChecklistItem = (itemId) => {
    const updatedChecklist = checklist.filter(item => item.id !== itemId);
    setChecklist(updatedChecklist);
    // Save draft to localStorage
    localStorage.setItem(cacheKey, JSON.stringify(updatedChecklist));
    toast.success('Checklist item removed');
  };

  const saveChecklistChanges = async () => {
    // Prevent multiple simultaneous saves
    if (isChecklistSaving) return;
    
    setIsChecklistSaving(true);
    
    try {
      // Cancel any previous save request
      if (abortRef.current) {
        abortRef.current.abort();
      }
      
      // Create new AbortController for this request
      abortRef.current = new AbortController();
      
      // Check if this is a real work order (has wo_id) or sample maintenance task
      if (task.wo_id || task.id) {
        // This is a real work order - save to backend
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const API = `${BACKEND_URL}/api`;
        
        const response = await axios.put(`${API}/work-orders/${task.id}`, {
          checklist: checklist
        }, {
          signal: abortRef.current.signal
        });
        
        // Use authoritative data from backend response
        const savedChecklist = response.data.checklist || checklist;
        setChecklist(savedChecklist);
        
        // Clear localStorage draft after successful save
        localStorage.removeItem(cacheKey);
        
        // Trigger cross-page synchronization with authoritative data
        const updateEvent = new CustomEvent('workOrderUpdated', {
          detail: { workOrderId: task.id, checklist: savedChecklist }
        });
        window.dispatchEvent(updateEvent);
        
        toast.success('Checklist saved successfully to database!');
      } else {
        // This is sample maintenance task data - save to localStorage with expiry
        const savedData = {
          checklist: checklist,
          timestamp: Date.now(),
          taskId: task.id || 'sample'
        };
        localStorage.setItem(`equiptrack:sample:${task.id || 'default'}`, JSON.stringify(savedData));
        
        // Clear draft since we've saved it
        localStorage.removeItem(cacheKey);
        
        toast.success('Sample maintenance task updated locally!');
      }
      
      // Don't call onUpdate to prevent modal from closing
      // Parent component will refresh data when modal is actually closed
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Save request was aborted');
        return; // Don't show error for aborted requests
      }
      
      console.error('Failed to save checklist:', error);
      toast.error('Failed to save checklist');
    } finally {
      setIsChecklistSaving(false);
      abortRef.current = null;
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        // Upload file to server
        const formData = new FormData();
        formData.append('file', file);
        
        const uploadResponse = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/files/upload`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        const fileMetadata = uploadResponse.data;
        const updatedFiles = [...files, fileMetadata];
        setFiles(updatedFiles);
        
        // Save file metadata to work order
        const response = await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/work-orders/${task.id}`,
          { files: updatedFiles },
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        console.log('File upload response:', response.data);
        
        // Update files from backend response
        if (response.data.files) {
          setFiles(response.data.files);
        }
        
        // Update parent component with new data
        if (onUpdate) {
          onUpdate(response.data);
        }
        
        // Dispatch event for cross-page sync
        const updateEvent = new CustomEvent('workOrderUpdated', {
          detail: response.data
        });
        window.dispatchEvent(updateEvent);
        
        toast.success(`File "${file.name}" uploaded and saved successfully`);
      } catch (error) {
        console.error('Error saving file:', error);
        toast.error('File uploaded but failed to save');
      }
    }
  };

  const removeFile = async (fileId) => {
    const updatedFiles = files.filter(file => file.id !== fileId);
    setFiles(updatedFiles);
    
    // Auto-save files immediately
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/work-orders/${task.id}`,
        { files: updatedFiles },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      console.log('File removal response:', response.data);
      
      // Update files from backend response
      if (response.data.files) {
        setFiles(response.data.files);
      }
      
      // Update parent component with new data
      if (onUpdate) {
        onUpdate(response.data);
      }
      
      // Dispatch event for cross-page sync
      const updateEvent = new CustomEvent('workOrderUpdated', {
        detail: response.data
      });
      window.dispatchEvent(updateEvent);
      
      toast.success('File removed and saved successfully');
    } catch (error) {
      console.error('Error saving after file removal:', error);
      toast.error('File removed but failed to save');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFrequencyIcon = (frequency) => {
    const icons = {
      'Daily': Clock,
      'Weekly': Calendar,
      'Monthly': Repeat
    };
    return icons[frequency] || Clock;
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Critical': 'bg-red-500 text-white',
      'High': 'bg-orange-500 text-white',
      'Medium': 'bg-yellow-500 text-white',
      'Low': 'bg-green-500 text-white'
    };
    return colors[priority] || 'bg-gray-500 text-white';
  };

  const completedItems = checklist.filter(item => item.completed).length;
  const totalItems = checklist.length;
  const progressPercentage = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden bg-white">
        <CardHeader className="pb-4 border-b">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              {/* Top: ID-Priority-Status badges */}
              <div className="flex items-center space-x-2 mb-2">
                <Badge variant="outline" className="font-mono text-xs">
                  {task.wo_id}
                </Badge>
                <Badge className={getPriorityColor(taskData.priority)}>
                  {taskData.priority}
                </Badge>
                <Badge className="bg-green-100 text-green-800">
                  {taskData.frequency} Maintenance
                </Badge>
              </div>
              
              {/* Title */}
              {editMode ? (
                <Input
                  value={taskData.title}
                  onChange={(e) => setTaskData(prev => ({ ...prev, title: e.target.value }))}
                  className="text-xl font-semibold mb-2"
                  data-testid="edit-task-title"
                />
              ) : (
                <CardTitle className="text-xl text-gray-900">{taskData.title}</CardTitle>
              )}
              
              {/* Requested by and date */}
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <span className="flex items-center">
                  <User className="w-4 h-4 mr-1" />
                  Requested by {task.requested_by_name || 'Unknown'}
                </span>
                <span className="flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  {new Date(task.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {!readOnly && !editMode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditMode(true)}
                  data-testid="edit-maintenance-task-btn"
                >
                  <Edit3 className="w-4 h-4 mr-2" />
                  {t('edit')}
                </Button>
              )}
              
              {!readOnly && editMode && (
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="save-maintenance-task-btn"
                >
                  {loading ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>{t('saving')}</span>
                    </div>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {t('save')}
                    </>
                  )}
                </Button>
              )}
              
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrint}
                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                data-testid="print-maintenance-task"
              >
                <Printer className="w-4 h-4 mr-2" />
                {t('print')}
              </Button>
              
              {!readOnly && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDelete}
                  disabled={loading || isChecklistSaving}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="delete-maintenance-task-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {t('delete')}
                </Button>
              )}
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  // Block closing during save
                  if (isChecklistSaving) {
                    toast.info('Please wait for checklist to save before closing');
                    return;
                  }
                  
                  // Check for unsaved changes (only if there's a draft AND we're in edit mode)
                  const currentDraft = localStorage.getItem(cacheKey);
                  if (currentDraft && editMode) {
                    const proceed = window.confirm('You have unsaved changes. Close anyway?');
                    if (!proceed) return;
                    
                    // Clear draft if user chooses to discard changes
                    localStorage.removeItem(cacheKey);
                  }
                  
                  if (onUpdate) {
                    // Refresh parent data when closing
                    onUpdate({ ...task, checklist });
                  }
                  onClose();
                }}
                className="h-8 w-8 p-0"
                data-testid="close-maintenance-task-detail"
                disabled={isChecklistSaving}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>

        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          <CardContent className="p-6">
            <Tabs defaultValue="details" className="space-y-6">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="details">{t('details')}</TabsTrigger>
                <TabsTrigger value="checklist">
                  {t('checklist')} ({completedItems}/{totalItems})
                </TabsTrigger>
                <TabsTrigger value="files">{t('files')}</TabsTrigger>
              </TabsList>

              <TabsContent value="details" className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-700">{t('department')}</Label>
                      {editMode ? (
                        <Select 
                          value={taskData.department_id} 
                          onValueChange={(value) => {
                            const dept = departments.find(d => d.id === value);
                            setTaskData(prev => ({ 
                              ...prev, 
                              department_id: value,
                              department_name: value === 'ALL_DEPARTMENTS' ? 'All Departments' : (dept?.name || ''),
                              machine_id: value === 'ALL_DEPARTMENTS' ? 'ALL_MACHINES' : '', // Auto-select All Machines for All Departments
                              machine_name: value === 'ALL_DEPARTMENTS' ? 'All Machines' : ''
                            }));
                          }}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder={t('selectDepartment')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ALL_DEPARTMENTS">
                              <span className="font-semibold text-blue-600">🏢 All Departments</span>
                            </SelectItem>
                            {departments.map(dept => (
                              <SelectItem key={dept.id} value={dept.id}>
                                {dept.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="mt-1 text-sm text-gray-900 p-2 bg-gray-50 rounded">
                          {taskData.department_name}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-gray-700">Machine/Equipment</Label>
                      {editMode ? (
                        <Select 
                          value={taskData.machine_id} 
                          onValueChange={(value) => {
                            const machine = machines.find(m => m.id === value);
                            setTaskData(prev => ({ 
                              ...prev, 
                              machine_id: value,
                              machine_name: value === 'ALL_MACHINES' ? 'All Machines' : (machine?.name || '')
                            }));
                          }}
                          disabled={!taskData.department_id}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder={taskData.department_id ? t('selectMachine') : t('selectDepartmentFirst')} />
                          </SelectTrigger>
                          <SelectContent>
                            {taskData.department_id === 'ALL_DEPARTMENTS' ? (
                              <SelectItem value="ALL_MACHINES">
                                <span className="font-semibold text-blue-600">⚙️ All Machines</span>
                              </SelectItem>
                            ) : (
                              <>
                                <SelectItem value="ALL_MACHINES">
                                  <span className="font-semibold text-blue-600">⚙️ All Machines</span>
                                </SelectItem>
                                {machines.map(machine => (
                                  <SelectItem key={machine.id} value={machine.id}>
                                    {machine.name}
                                  </SelectItem>
                                ))}
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="mt-1 text-sm text-gray-900 p-2 bg-gray-50 rounded">
                          {taskData.machine_name}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-gray-700">Assignees (Multiple)</Label>
                      {editMode ? (
                        <MultiSelect
                          options={users.map(u => ({ value: u.id, label: u.name || u.username }))}
                          value={taskData.assignees}
                          onChange={(selectedIds) => setTaskData(prev => ({ ...prev, assignees: selectedIds }))}
                          placeholder="Select assignees..."
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-1 text-sm text-gray-900 p-2 bg-gray-50 rounded">
                          {taskData.assignee_names && taskData.assignee_names.length > 0 
                            ? taskData.assignee_names.join(', ') 
                            : 'Unassigned'}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-gray-700">Priority</Label>
                      {editMode ? (
                        <Select 
                          value={taskData.priority} 
                          onValueChange={(value) => setTaskData(prev => ({ ...prev, priority: value }))}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Low">{t('low')}</SelectItem>
                            <SelectItem value="Medium">{t('medium')}</SelectItem>
                            <SelectItem value="High">{t('high')}</SelectItem>
                            <SelectItem value="Critical">{t('critical')}</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="mt-1 text-sm text-gray-900 p-2 bg-gray-50 rounded">
                          {taskData.priority}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Frequency</Label>
                      {editMode ? (
                        <Select 
                          value={taskData.frequency} 
                          onValueChange={(value) => setTaskData(prev => ({ ...prev, frequency: value }))}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Daily">Daily</SelectItem>
                            <SelectItem value="Weekly">Weekly</SelectItem>
                            <SelectItem value="Monthly">Monthly</SelectItem>
                            <SelectItem value="Yearly">Yearly</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="mt-1 text-sm text-gray-900 p-2 bg-gray-50 rounded">
                          {taskData.frequency}
                        </p>
                      )}
                    </div>

                    {/* Day of Week field for Weekly tasks */}
                    {taskData.frequency === 'Weekly' && (
                      <div>
                        <Label className="text-sm font-medium text-gray-700">Day of Week</Label>
                        {editMode ? (
                          <Select 
                            value={taskData.dayOfWeek} 
                            onValueChange={(value) => setTaskData(prev => ({ ...prev, dayOfWeek: value }))}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Monday">Monday</SelectItem>
                              <SelectItem value="Tuesday">Tuesday</SelectItem>
                              <SelectItem value="Wednesday">Wednesday</SelectItem>
                              <SelectItem value="Thursday">Thursday</SelectItem>
                              <SelectItem value="Friday">Friday</SelectItem>
                              <SelectItem value="Saturday">Saturday</SelectItem>
                              <SelectItem value="Sunday">Sunday</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-sm text-gray-900 p-2 bg-gray-50 rounded">
                            {taskData.dayOfWeek}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Period of Month field for Monthly tasks */}
                    {taskData.frequency === 'Monthly' && (
                      <div>
                        <Label className="text-sm font-medium text-gray-700">Period of Month</Label>
                        {editMode ? (
                          <Select 
                            value={taskData.monthlyPeriod} 
                            onValueChange={(value) => setTaskData(prev => ({ ...prev, monthlyPeriod: value }))}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="start">Start of Month (Mon-Fri)</SelectItem>
                              <SelectItem value="middle">Middle of Month (Mon-Fri)</SelectItem>
                              <SelectItem value="end">End of Month (Mon-Fri)</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-sm text-gray-900 p-2 bg-gray-50 rounded">
                            {taskData.monthlyPeriod === 'start' ? 'Start of Month (Mon-Fri)' : 
                             taskData.monthlyPeriod === 'middle' ? 'Middle of Month (Mon-Fri)' : 
                             'End of Month (Mon-Fri)'}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Month field for Yearly tasks */}
                    {taskData.frequency === 'Yearly' && (
                      <div>
                        <Label className="text-sm font-medium text-gray-700">Month</Label>
                        {editMode ? (
                          <Select 
                            value={taskData.yearlyMonth} 
                            onValueChange={(value) => setTaskData(prev => ({ ...prev, yearlyMonth: value }))}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="January">January</SelectItem>
                              <SelectItem value="February">February</SelectItem>
                              <SelectItem value="March">March</SelectItem>
                              <SelectItem value="April">April</SelectItem>
                              <SelectItem value="May">May</SelectItem>
                              <SelectItem value="June">June</SelectItem>
                              <SelectItem value="July">July</SelectItem>
                              <SelectItem value="August">August</SelectItem>
                              <SelectItem value="September">September</SelectItem>
                              <SelectItem value="October">October</SelectItem>
                              <SelectItem value="November">November</SelectItem>
                              <SelectItem value="December">December</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-sm text-gray-900 p-2 bg-gray-50 rounded">
                            {taskData.yearlyMonth}
                          </p>
                        )}
                      </div>
                    )}

                    <div>
                      <Label className="text-sm font-medium text-gray-700">Scheduled Time</Label>
                      {editMode ? (
                        <Input
                          type="time"
                          value={taskData.time}
                          onChange={(e) => setTaskData(prev => ({ ...prev, time: e.target.value }))}
                          className="mt-1"
                        />
                      ) : (
                        <p className="mt-1 text-sm text-gray-900 p-2 bg-gray-50 rounded">
                          {taskData.time}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-gray-700">Task ID</Label>
                      <p className="mt-1 text-sm font-mono text-gray-600 p-2 bg-gray-50 rounded">
                        MT-{task.id || '001'}
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                <div>
                  <Label className="text-sm font-medium text-gray-700">Instructions</Label>
                  {editMode ? (
                    <Textarea
                      value={taskData.notes}
                      onChange={(e) => setTaskData(prev => ({ ...prev, notes: e.target.value }))}
                      rows={4}
                      className="mt-1"
                      placeholder={t('detailedMaintenanceInstructions')}
                    />
                  ) : (
                    <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap p-3 bg-gray-50 rounded-lg">
                      {taskData.notes || 'No instructions provided.'}
                    </p>
                  )}
                </div>

                <div>
                  <Label className="text-sm font-medium text-gray-700">Safety Notes</Label>
                  {editMode ? (
                    <Textarea
                      value={taskData.safety_notes}
                      onChange={(e) => setTaskData(prev => ({ ...prev, safety_notes: e.target.value }))}
                      rows={3}
                      className="mt-1"
                      placeholder={t('importantSafetyInfo')}
                    />
                  ) : (
                    <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      {taskData.safety_notes || 'No safety notes specified.'}
                    </p>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="checklist" className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">Maintenance Checklist</h3>
                  <div className="text-sm text-gray-600">
                    {completedItems} of {totalItems} completed ({Math.round(progressPercentage)}%)
                  </div>
                </div>

                {totalItems > 0 && (
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                    <div 
                      className="bg-green-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progressPercentage}%` }}
                    ></div>
                  </div>
                )}

                {/* Add new checklist item - only in edit mode */}
                {editMode && (
                  <div className="flex space-x-2 p-4 bg-green-50 rounded-lg">
                    <Input
                      type="text"
                      placeholder="Add checklist item (e.g., Check oil levels, Inspect belts)"
                      value={newChecklistItem}
                      onChange={(e) => setNewChecklistItem(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addChecklistItem()}
                      className="flex-1"
                      data-testid="new-maintenance-checklist-item"
                    />
                    <Button
                      onClick={addChecklistItem}
                      disabled={!newChecklistItem.trim()}
                      size="sm"
                      data-testid="add-maintenance-checklist-item"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                )}

                <div className="space-y-3">
                  {checklist.map((item, index) => (
                    <div key={item.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                      <Checkbox
                        id={`maintenance-checklist-${item.id}`}
                        checked={item.completed}
                        onCheckedChange={(checked) => handleChecklistToggle(item.id, checked)}
                        className="mt-0.5"
                        disabled={!editMode}
                        data-testid={`maintenance-checklist-item-${index}`}
                      />
                      <div className="flex-1">
                        <Label
                          htmlFor={`maintenance-checklist-${item.id}`}
                          className={`text-sm cursor-pointer ${
                            item.completed ? 'line-through text-gray-500' : 'text-gray-900'
                          }`}
                        >
                          {item.text}
                        </Label>
                        <div className="text-xs text-gray-500 mt-1">
                          Added by {item.created_by} on {formatDate(item.created_at)}
                          {item.completed && item.completed_by && (
                            <span> • Completed by {item.completed_by}</span>
                          )}
                        </div>
                      </div>
                      {editMode && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeChecklistItem(item.id)}
                          className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Standalone Save Checklist button removed - saving integrated with main Edit/Save button */}

                {totalItems === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <CheckSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p>No checklist items yet. Add maintenance procedures above.</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="files" className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium">Files & Documentation</h3>
                  <div className="flex items-center space-x-2">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="maintenance-file-upload"
                    />
                    <Label htmlFor="maintenance-file-upload">
                      <Button variant="outline" size="sm" className="cursor-pointer" asChild>
                        <span>
                          <Upload className="w-4 h-4 mr-2" />
                          Upload File
                        </span>
                      </Button>
                    </Label>
                  </div>
                </div>

                <div className="space-y-3">
                  {files.map((file) => (
                    <div key={file.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{file.name}</p>
                          <p className="text-sm text-gray-500">
                            {formatFileSize(file.size)} • Uploaded by {file.uploaded_by} on {formatDate(file.uploaded_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            try {
                              const response = await axios.get(
                                `${process.env.REACT_APP_BACKEND_URL}/api/files/download/${file.id}?stored_filename=${file.stored_filename}`,
                                {
                                  responseType: 'blob',
                                  headers: {
                                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                                  }
                                }
                              );
                              
                              // Create blob URL and trigger download
                              const blob = new Blob([response.data]);
                              const url = window.URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = url;
                              link.download = file.name;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                              window.URL.revokeObjectURL(url);
                            } catch (error) {
                              console.error('Download error:', error);
                              toast.error('Failed to download file');
                            }
                          }}
                          className="h-8 w-8 p-0"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(file.id)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                {files.length === 0 && (
                  <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                    <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="mb-2">No files uploaded yet</p>
                    <p className="text-sm">Upload manuals, diagrams, or documentation for this maintenance task</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </div>
      </Card>
    </div>
  );
}

export default MaintenanceTaskDetail;